import {Avatar, AvatarFallback, AvatarImage} from '@orbit-ui/components/avatar';
import {Button} from '@orbit-ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@orbit-ui/components/dropdown-menu';
import {Skeleton} from '@orbit-ui/components/skeleton';
import {useIsMobile} from '@orbit-ui/core';
import {Link} from '@tanstack/react-router';
import {useAuth} from '@workos-inc/authkit-react';
import {ChevronsUpDownIcon, ClockIcon, LogOutIcon, UserCog2Icon} from 'lucide-react';
import * as React from 'react';

// Create a type that works with both versions
type User = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePictureUrl?: string;
  // Add any other properties you need
};

function UserMenu() {
  const {user, signOut} = useAuth();
  const isMobile = useIsMobile();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="flex w-full min-w-48 flex-row gap-2 px-2 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
        >
          <UserProfile user={user as User | null} />
          <ChevronsUpDownIcon className="ml-auto size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] rounded-lg"
        side={isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <UserProfile user={user as User | null} />
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {/*
          // TODO: Link to workos dashboard
          <DropdownMenuItem>
            <Link to="/settings/account">
              <UserCogIcon className="mr-2 size-4" />
              Account
            </Link>
          </DropdownMenuItem>
          */}
          <DropdownMenuItem asChild>
            <Link to="/settings/user">
              <UserCog2Icon className="mr-2 size-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/events">
              <ClockIcon className="mr-2 size-4" />
              <span>Event History</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()}>
          <LogOutIcon className="mr-2 size-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const UserProfile = React.memo(UserProfileRaw);
function UserProfileRaw({user}: {user: User | null}) {
  if (!user) {
    return (
      <>
        <Avatar className="size-8 rounded-lg">
          <AvatarFallback className="rounded-lg"></AvatarFallback>
        </Avatar>
        <div className="mr-auto grid flex-1 text-left text-sm leading-tight">
          <Skeleton className="mb-0.5 h-3.5 w-3/4" />
          <Skeleton className="h-2.5 w-11/12" />
        </div>
      </>
    );
  }

  // User is guaranteed to be non-null at this point
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const {email} = user;
  const initial = user.firstName ? user.firstName.charAt(0) : '';

  return (
    <>
      <Avatar className="size-8 rounded-lg">
        <AvatarImage src={user.profilePictureUrl ?? ''} alt={name} />
        <AvatarFallback className="rounded-lg">{initial}</AvatarFallback>
      </Avatar>
      <div className="mr-auto grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-semibold">{name}</span>
        <span className="truncate text-xs">{email}</span>
      </div>
    </>
  );
}

export {UserMenu};
